import { createI18n } from "vue-i18n";
import en from "@/locales/en.json";
import it from "@/locales/it.json";
import fr from "@/locales/fr.json";
import de from "@/locales/de.json";
import es from "@/locales/es.json";
import pl from "@/locales/pl.json";
import { DEFAULT_LOCALE, type Locale } from "@/seo/routes-manifest";

export type AppLocale = Locale;

export const LOCALE_STORAGE_KEY = "cresci-locale";

export const LOCALE_LABELS: Record<AppLocale, string> = {
  en: "English",
  it: "Italiano",
  fr: "Français",
  de: "Deutsch",
  es: "Español",
  pl: "Polski",
};

export const SUPPORTED_LOCALES = Object.keys(LOCALE_LABELS) as AppLocale[];

export function createAppI18n(initialLocale: AppLocale = DEFAULT_LOCALE) {
  return createI18n<object, AppLocale, false>({
    legacy: false,
    locale: initialLocale,
    fallbackLocale: DEFAULT_LOCALE,
    messages: { en, it, fr, de, es, pl },
  });
}

export function detectBrowserLocale(): AppLocale {
  if (typeof navigator === "undefined") return DEFAULT_LOCALE;
  try {
    const lang = navigator.language.split("-")[0];
    if (SUPPORTED_LOCALES.includes(lang as AppLocale)) {
      return lang as AppLocale;
    }
  } catch {
    // SSR or restricted environment
  }
  return DEFAULT_LOCALE;
}

export function readPreferredLocale(): AppLocale | null {
  if (typeof localStorage === "undefined") return null;
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (stored && SUPPORTED_LOCALES.includes(stored as AppLocale)) {
      return stored as AppLocale;
    }
  } catch {
    // localStorage unavailable
  }
  return null;
}

export function writePreferredLocale(locale: AppLocale): void {
  if (typeof localStorage === "undefined") return;
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // localStorage unavailable
  }
}
